import { memo, useCallback, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Separator from "../components/Separator.tsx";
import IconButton from "../components/IconButton.tsx";
import { getFileType } from "../utils/validators.ts";
import { get } from "http";
import ModalConfirmacao2 from "../../components/ModalConfirmacao2/ModalConfirmacao2.tsx";

interface Images {
	id: number;
	url: string;
}

interface MultiImageCarouselProps {
	images: Images[];
	chunkSize?: number;
	onSelectImage?: (index: number) => void;
	onDeleteImage?: (id: number) => void;
}

const GAP = 15;
const slideVariants = {
	enter: (d: number) => ({ x: d > 0 ? 300 : -300, opacity: 0 }),
	center: { x: 0, opacity: 1 },
	exit: (d: number) => ({ x: d > 0 ? -300 : 300, opacity: 0 }),
};

function MultiImageCarousel({
	images,
	chunkSize = 5,
	onSelectImage = () => {},
	onDeleteImage = () => {},
}: MultiImageCarouselProps) {
	const imageUrls = useMemo(() => images.map((image) => image.url), [images]);

	const chunks = useMemo(() => {
		const r: string[][] = [];
		for (let i = 0; i < images.length; i += chunkSize)
			r.push(imageUrls.slice(i, i + chunkSize));
		return r;
	}, [images, chunkSize]);

	const [idx, setIdx] = useState(0);
	const [dir, setDir] = useState(0);
	const [selectedId, setSelectedId] = useState(0);

	const next = useCallback(() => {
		if (idx < chunks.length - 1) {
			setDir(1);
			setIdx((i) => i + 1);
		}
	}, [idx, chunks.length]);

	const prev = useCallback(() => {
		if (idx > 0) {
			setDir(-1);
			setIdx((i) => i - 1);
		}
	}, [idx]);

	const handleDownload = useCallback(
		(img: string, i: number, e: React.MouseEvent) => {
			e.stopPropagation();
			e.preventDefault();
			window.open(img, "_blank");
		},
		[]
	);

	if (!chunks.length) return null;

	const cur = chunks[idx];
	const imgW = `calc((100% - ${(cur.length - 1) * GAP}px) / ${cur.length})`;
	return (
		<motion.div
			layout
			className="multi-carousel-container"
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			transition={{ duration: 0.3 }}
			style={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				width: "100%",
				height: "300px",
			}}
		>
			<IconButton
				backgroundColor="#878787"
				icon="ChevronLeft"
				onClick={prev}
				size="lg"
				disabled={idx === 0}
			/>
			<Separator direction="horizontal" size="md" />
			<div
				style={{
					flex: 1,
					position: "relative",
					height: "100%",
					borderRadius: 8,
				}}
			>
				<AnimatePresence custom={dir} initial={false}>
					<motion.div
						layout
						key={idx}
						custom={dir}
						variants={slideVariants}
						initial="enter"
						animate="center"
						exit="exit"
						className="multi-carousel-images"
						transition={{
							x: { type: "spring", stiffness: 200, damping: 30 },
							opacity: { duration: 0.2 },
						}}
						style={{
							position: "absolute",
							inset: 0,
							display: "flex",
							gap: GAP,
							height: "300px",
						}}
					>
						{chunks[idx].map((img, i) => {
							return (
								<motion.div
									layout
									key={img}
									style={{
										width: imgW,
										position: "relative",
										maxWidth: "300px",
										maxHeight: "300px",
										borderRadius: 8,
									}}
									onClick={() => onSelectImage(idx * chunkSize + i)}
								>
									{getFileType(img) === "image" && (
										<>
											<motion.img
												key={img + "image"}
												whileHover={{ scale: 1.05 }}
												whileTap={{ scale: 0.95 }}
												src={img}
												alt={`Imagem ${idx * chunkSize + i}`}
												style={{
													width: "100%",
													height: "100%",
													objectFit: "cover",
													borderRadius: 8,
													cursor: "pointer",
												}}
											/>
											<div
												style={{
													position: "absolute",
													top: 8,
													right: 8,
													zIndex: 2,
													display: "flex",
													alignItems: "center",
												}}
											>
												<IconButton
													backgroundColor="#fff"
													hoverColor="#f0f0f0"
													theme="dark"
													icon="Download"
													size="sm"
													onClick={(e) => handleDownload(img, i, e)}
												/>
												<Separator direction="horizontal" size="sm" />
												<IconButton
													backgroundColor="#fff"
													hoverColor="#f0f0f0"
													theme="dark"
													icon="Trash"
													size="sm"
													onClick={(e) => {
														e.stopPropagation();
														e.preventDefault();
														setSelectedId(images[idx * chunkSize + i].id);
													}}
												/>
											</div>
										</>
									)}
									{getFileType(img) === "pdf" && (
										<embed
											key={img + "pdf"}
											src={img}
											type="application/pdf"
											width="100%"
											height="100%"
											style={{
												borderRadius: 8,
												cursor: "pointer",
											}}
										/>
									)}
								</motion.div>
							);
						})}
					</motion.div>
				</AnimatePresence>
			</div>
			<Separator direction="horizontal" size="md" />
			<IconButton
				backgroundColor="#878787"
				icon="ChevronRight"
				onClick={next}
				size="lg"
				disabled={idx === chunks.length - 1}
			/>
			<ModalConfirmacao2
				titulo="Deletar Imagem"
				texto="Tem certeza que deseja deletar esta imagem?"
				textoBotao="Sim"
				open={!!selectedId}
				onClose={() => {
					setSelectedId(0);
				}}
				onClick={() => {
					onDeleteImage(selectedId);
					setSelectedId(0);
				}}
			/>
		</motion.div>
	);
}

export default memo(MultiImageCarousel);
